/**
 * "Connect Meta via System User" — Control Center section.
 *
 * Lets an owner / team_lead provision a NON-EXPIRING system-user token for the
 * active brand and map it to the right ad account. Built in the legacy SCSS
 * style (no MUI): the shared `Popup`, `MainSquareButton`, Redux `setMessage`
 * toasts — matching the rest of the Control Center.
 *
 * Two-step flow inside one Popup:
 *   1. Paste the system-user token (password input) -> "Validate & fetch ad
 *      accounts" -> validateSystemToken().
 *   2. Pick the ad account that is THIS client from the returned list ->
 *      "Connect" -> connectSystemToken() -> success toast + status refresh.
 *
 * Connected state shows "System user · {ad account}" with a connection date and
 * a Disconnect/Replace affordance. When the backend reports a `user` (OAuth /
 * 60-day) token, a subtle nudge invites upgrading to a system-user token.
 *
 * RBAC: the WHOLE affordance is gated to owner / team_lead. `integrations.manage`
 * is manager+, so we deliberately add an explicit role check on top of it — a
 * manager/analyst/read_only never sees this section.
 *
 * Scope: the active brand (`user.profile.active_shop_slug`), read live so the
 * Control Center brand switcher re-scopes it.
 *
 * SECURITY: the token only ever lives in this component's in-memory state and
 * is passed in the request body. It is never persisted or logged.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import MainSquareButton from '../../ui/buttons/MainSquareButton';
import Popup from '../../ui/popup/Popup';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import {
  keySVG,
  linkSVG,
  trashSVG,
  reloadArrowsSVG,
  infoSVG,
  tickWithCircleSVG,
} from '../../lib/allCustomSVGs';
import { RootState } from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import { useEmployeeRole } from '../../lib/rbac';
import type { EmployeeRole } from '../../lib/rbac';
import {
  MetaAdAccount,
  MetaStatusResponse,
  connectSystemToken,
  getMetaStatus,
  validateSystemToken,
} from '../../lib/api/integrations/meta-system-user-api';
import { disconnectIntegration } from '../../lib/api/integrations/integrations-api';

/** The platform key the backend uses for the Meta integration. */
const META_PLATFORM_KEY = 'meta';

// ---------- helpers ----------

/**
 * Whether `role` may use the System-User connect flow. `integrations.manage`
 * is broad, but provisioning a non-expiring agency credential is an
 * owner/manager governance action — so gate explicitly on those two roles.
 */
function canConnectSystemUser(role: EmployeeRole | null): boolean {
  return role === 'owner' || role === 'manager';
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/** Meta `account_status === 1` means active; anything else is flagged. */
function isAccountActive(status: number | string): boolean {
  return String(status) === '1';
}

type Step = 'token' | 'accounts';

const MetaSystemUserSection = ({
  compact = false,
}: {
  // `compact` drops the standalone section heading so this can be embedded
  // directly on the Facebook Ads integration card (its connect/connected CTA +
  // the 2-step popup are unchanged).
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const role = useEmployeeRole();
  const canManage = canConnectSystemUser(role);

  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug
  );
  const agencyToken = useSelector(
    (s: RootState) => s.auth?.agency?.accessToken ?? ''
  );
  // Guards use the boolean, but effects must key on the token VALUE: the
  // admin "login as seller" handoff swaps the agency session mid-page, and a
  // boolean dep would skip the refetch and keep stale wrong-org data.
  const hasAgencyToken = Boolean(agencyToken);

  // ----------------------- status -----------------------
  const [status, setStatus] = useState<MetaStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // ----------------------- popup / 2-step flow -----------------------
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('token');
  const [token, setToken] = useState('');
  const [validating, setValidating] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [adAccounts, setAdAccounts] = useState<MetaAdAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null
  );
  const [businessName, setBusinessName] = useState<string | null>(null);

  // ----------------------- disconnect confirm -----------------------
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const showError = (msg: string) =>
    dispatch(setMessage({ messageType: 'error', message: msg }));
  const showSuccess = (msg: string) =>
    dispatch(setMessage({ messageType: 'success', message: msg }));
  const showInfo = (msg: string) =>
    dispatch(setMessage({ messageType: 'info', message: msg }));

  const refreshStatus = useCallback(async (slug: string) => {
    setStatusLoading(true);
    try {
      const next = await getMetaStatus(slug);
      setStatus(next);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[MetaSystemUser] status load failed', err);
      // A missing/404 status simply means "not connected" — don't alarm.
      setStatus({ token_kind: null, ad_account_id: null, connected_at: null });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage && activeShopSlug && hasAgencyToken) {
      refreshStatus(activeShopSlug);
    } else {
      setStatus(null);
    }
    // close transient UI on brand switch
    setOpen(false);
    setConfirmDisconnect(false);
  }, [canManage, activeShopSlug, agencyToken, refreshStatus]);

  // ----------------------- popup lifecycle -----------------------
  const resetFlow = () => {
    setStep('token');
    setToken('');
    setFormError(null);
    setAdAccounts([]);
    setSelectedAccountId(null);
    setBusinessName(null);
    setValidating(false);
    setConnecting(false);
  };

  const openFlow = () => {
    resetFlow();
    setOpen(true);
  };

  const closeFlow = () => {
    if (validating || connecting) return;
    setOpen(false);
    // Clear the token from memory as soon as the popup closes.
    resetFlow();
  };

  // ----------------------- step 1: validate -----------------------
  const handleValidate = async () => {
    if (!activeShopSlug || !canManage) return;
    const trimmed = token.trim();
    if (!trimmed) {
      setFormError(t('MetaSysUserTokenRequired'));
      return;
    }
    setValidating(true);
    setFormError(null);
    try {
      const res = await validateSystemToken(activeShopSlug, trimmed);
      if (!res.valid) {
        setFormError(res.error || t('MetaSysUserInvalidToken'));
        return;
      }
      if (res.ad_accounts.length === 0) {
        setFormError(t('MetaSysUserNoAccounts'));
        return;
      }
      setAdAccounts(res.ad_accounts);
      setBusinessName(res.business?.name ?? null);
      setSelectedAccountId(res.ad_accounts[0]?.id ?? null);
      setStep('accounts');
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[MetaSystemUser] validate failed', err);
      setFormError(err?.message || t('MetaSysUserValidateFailed'));
    } finally {
      setValidating(false);
    }
  };

  // ----------------------- step 2: connect -----------------------
  const handleConnect = async () => {
    if (!activeShopSlug || !canManage) return;
    if (!selectedAccountId) {
      setFormError(t('MetaSysUserSelectAccount'));
      return;
    }
    setConnecting(true);
    setFormError(null);
    try {
      const res = await connectSystemToken(
        activeShopSlug,
        token.trim(),
        selectedAccountId
      );
      if (!res.connected) {
        setFormError(t('MetaSysUserConnectFailed'));
        return;
      }
      showSuccess(
        t('MetaSysUserConnected', {
          name: res.ad_account?.name || selectedAccountId,
        })
      );
      setOpen(false);
      resetFlow();
      await refreshStatus(activeShopSlug);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[MetaSystemUser] connect failed', err);
      setFormError(err?.message || t('MetaSysUserConnectFailed'));
    } finally {
      setConnecting(false);
    }
  };

  // ----------------------- disconnect -----------------------
  // Disconnect reuses the standard platform DELETE in `integrations-api`
  // (`DELETE …/integrations/meta`), revoking the stored system-user token +
  // ad-account mapping. "Replace" instead re-opens the connect flow over the
  // existing mapping (the backend overwrites token + ad account on connect).
  const handleConfirmDisconnect = async () => {
    if (!activeShopSlug) return;
    setDisconnecting(true);
    try {
      await disconnectIntegration(activeShopSlug, META_PLATFORM_KEY);
      showInfo(t('MetaSysUserDisconnected'));
      setConfirmDisconnect(false);
      await refreshStatus(activeShopSlug);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[MetaSystemUser] disconnect failed', err);
      showError(err?.message || t('MetaSysUserDisconnectFailed'));
    } finally {
      setDisconnecting(false);
    }
  };

  // ----------------------- derived display -----------------------
  const connectedAccountLabel = useMemo(() => {
    if (!status) return '';
    return (
      status.ad_account_name ||
      status.ad_account_id ||
      t('MetaSysUserUnknownAccount')
    );
  }, [status, t]);

  const isSystemUserConnected = status?.token_kind === 'system_user';
  const isOauthConnected = status?.token_kind === 'user';

  // ----------------------- gate: hidden entirely for non-managers -----------
  if (!canManage) return null;
  if (!activeShopSlug || !hasAgencyToken) return null;

  // ----------------------- render -----------------------
  // `compact` (embedded on the Facebook card) drops the standalone section
  // chrome + heading; everything below (status body + 2-step popup) is shared.
  return (
    <div className={compact ? 'metaSysUserCompact' : 'controlCenter__section'}>
      {compact ? (
        <span className='metaSysUserCompact__label'>{t('MetaSysUserTitle')}</span>
      ) : (
        <div className='controlCenter__sectionHeader'>
          <div className='controlCenter__sectionHeader-text'>
            <h2>{t('MetaSysUserTitle')}</h2>
            <p>{t('MetaSysUserSubtitle')}</p>
          </div>
        </div>
      )}

      <div className='metaSysUser'>
        {statusLoading ? (
          <LoadingSpinner />
        ) : isSystemUserConnected ? (
          // ---------- connected via system user ----------
          <div className='metaSysUser__connected'>
            <div className='metaSysUser__connected-main'>
              <span className='metaSysUser__connected-icon'>
                <CustomSVGs svg={tickWithCircleSVG} />
              </span>
              <div className='metaSysUser__connected-text'>
                <span className='metaSysUser__connected-title'>
                  {t('MetaSysUserConnectedLabel', {
                    account: connectedAccountLabel,
                  })}
                </span>
                {status?.connected_at && (
                  <span className='metaSysUser__connected-meta'>
                    {t('MetaSysUserConnectedSince')}:{' '}
                    {formatTimestamp(status.connected_at)}
                  </span>
                )}
              </div>
              <span className='ccPill ccPill--connected'>
                {t('MetaSysUserKindSystem')}
              </span>
            </div>
            <div className='metaSysUser__connected-actions'>
              <button
                type='button'
                className='metaSysUser__iconBtn'
                onClick={openFlow}
              >
                <CustomSVGs svg={reloadArrowsSVG} />
                {t('MetaSysUserReplace')}
              </button>
              <button
                type='button'
                className='metaSysUser__iconBtn metaSysUser__iconBtn--danger'
                onClick={() => setConfirmDisconnect(true)}
              >
                <CustomSVGs svg={trashSVG} />
                {t('MetaSysUserDisconnect')}
              </button>
            </div>
          </div>
        ) : (
          // ---------- not connected via system user ----------
          <div className='metaSysUser__cta'>
            {isOauthConnected && (
              <div className='metaSysUser__nudge'>
                <span className='metaSysUser__nudge-icon'>
                  <CustomSVGs svg={infoSVG} />
                </span>
                <span>{t('MetaSysUserOauthNudge')}</span>
              </div>
            )}
            <div className='metaSysUser__cta-row'>
              <div className='metaSysUser__cta-text'>
                <span className='metaSysUser__cta-title'>
                  {t('MetaSysUserCtaTitle')}
                </span>
                <span className='metaSysUser__cta-sub'>
                  {t('MetaSysUserCtaSubtitle')}
                </span>
              </div>
              <MainSquareButton
                value={t('MetaSysUserConnectAction')}
                icon={<CustomSVGs svg={keySVG} />}
                type='button'
                normal={true}
                onClick={openFlow}
              />
            </div>
          </div>
        )}
      </div>

      {/* ---------- 2-step connect popup ---------- */}
      <Popup
        styles={{ backgroundColor: 'var(--containers-background-color)' }}
        trigger={open}
        setTrigger={closeFlow}
      >
        <div className='ccPopup metaSysUser__popup'>
          <h2 className='ccPopup__title'>{t('MetaSysUserPopupTitle')}</h2>

          {/* step indicator */}
          <div className='metaSysUser__steps'>
            <span
              className='metaSysUser__step'
              data-active={step === 'token' ? 'true' : 'false'}
            >
              1. {t('MetaSysUserStepToken')}
            </span>
            <span className='metaSysUser__step-sep' />
            <span
              className='metaSysUser__step'
              data-active={step === 'accounts' ? 'true' : 'false'}
            >
              2. {t('MetaSysUserStepAccount')}
            </span>
          </div>

          {step === 'token' ? (
            <>
              <span className='ccPopup__subtitle'>
                {t('MetaSysUserTokenHelper')}
              </span>
              <div className='ccPopup__form'>
                <div className='ccPopup__field'>
                  <label htmlFor='meta-sys-token'>
                    {t('MetaSysUserTokenLabel')}
                  </label>
                  <input
                    id='meta-sys-token'
                    type='password'
                    autoComplete='off'
                    spellCheck={false}
                    value={token}
                    placeholder={t('MetaSysUserTokenPlaceholder')}
                    disabled={validating}
                    onChange={(e) => setToken(e.target.value)}
                  />
                </div>
                {formError && (
                  <div className='ccPopup__formError'>{formError}</div>
                )}
              </div>
              <div className='ccPopup__actions'>
                <MainSquareButton
                  value={t('Cancel')}
                  buttonType='white'
                  type='button'
                  normal={true}
                  disabled={validating}
                  onClick={closeFlow}
                />
                <MainSquareButton
                  value={t('MetaSysUserValidateAction')}
                  icon={<CustomSVGs svg={linkSVG} />}
                  type='button'
                  normal={true}
                  loading={validating}
                  disabled={validating}
                  onClick={handleValidate}
                />
              </div>
            </>
          ) : (
            <>
              <span className='ccPopup__subtitle'>
                {businessName
                  ? t('MetaSysUserPickAccountFor', { business: businessName })
                  : t('MetaSysUserPickAccount')}
              </span>

              <div
                className='metaSysUser__accountList'
                role='radiogroup'
                aria-label={t('MetaSysUserPickAccount')}
              >
                {adAccounts.map((acct) => {
                  const selected = selectedAccountId === acct.id;
                  const active = isAccountActive(acct.account_status);
                  return (
                    <button
                      key={acct.id}
                      type='button'
                      role='radio'
                      aria-checked={selected}
                      className='metaSysUser__account'
                      data-selected={selected ? 'true' : 'false'}
                      disabled={connecting}
                      onClick={() => setSelectedAccountId(acct.id)}
                    >
                      <span className='metaSysUser__account-radio' />
                      <span className='metaSysUser__account-body'>
                        <span className='metaSysUser__account-name'>
                          {acct.name}
                        </span>
                        <span className='metaSysUser__account-meta'>
                          {acct.currency} · {acct.id}
                        </span>
                      </span>
                      <span
                        className='metaSysUser__account-status'
                        data-active={active ? 'true' : 'false'}
                      >
                        {active
                          ? t('MetaSysUserAccountActive')
                          : t('MetaSysUserAccountInactive')}
                      </span>
                    </button>
                  );
                })}
              </div>

              {formError && (
                <div className='ccPopup__formError'>{formError}</div>
              )}

              <div className='ccPopup__actions'>
                <MainSquareButton
                  value={t('Back')}
                  buttonType='white'
                  type='button'
                  normal={true}
                  disabled={connecting}
                  onClick={() => {
                    setFormError(null);
                    setStep('token');
                  }}
                />
                <MainSquareButton
                  value={t('MetaSysUserConnectButton')}
                  icon={<CustomSVGs svg={linkSVG} />}
                  type='button'
                  normal={true}
                  loading={connecting}
                  disabled={connecting || !selectedAccountId}
                  onClick={handleConnect}
                />
              </div>
            </>
          )}
        </div>
      </Popup>

      {/* ---------- disconnect confirm popup ---------- */}
      <Popup
        styles={{ backgroundColor: 'var(--containers-background-color)' }}
        trigger={confirmDisconnect}
        setTrigger={() => {
          if (!disconnecting) setConfirmDisconnect(false);
        }}
      >
        <div className='ccPopup'>
          <h2 className='ccPopup__title'>{t('MetaSysUserDisconnectTitle')}</h2>
          <div className='ccPopup__confirmBody'>
            {t('MetaSysUserDisconnectBody')}{' '}
            <strong>{connectedAccountLabel}</strong>.
          </div>
          <div className='ccPopup__actions'>
            <MainSquareButton
              value={t('Cancel')}
              buttonType='white'
              type='button'
              normal={true}
              disabled={disconnecting}
              onClick={() => setConfirmDisconnect(false)}
            />
            <MainSquareButton
              value={t('MetaSysUserDisconnect')}
              icon={<CustomSVGs svg={trashSVG} />}
              buttonType='danger'
              type='button'
              normal={true}
              loading={disconnecting}
              disabled={disconnecting}
              onClick={handleConfirmDisconnect}
            />
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default MetaSystemUserSection;
